/**
 * Editor state. The project is immutable (Immer); every change goes through `edit`, which records
 * the previous state for undo. Thanks to structural sharing, keeping old states costs little.
 *
 * Drags are *gestures*: `begin()` before the first update and `end()` after the last turn the
 * whole drag into one undo step.
 */

import { produce, type Draft } from "immer";
import { create } from "zustand";
import type { Project } from "@studio/engine";
import type { AnalysisState, Job, ReviewComment } from "../api";

export type Selection = { kind: "element" | "clip" | "scene" | "camkey" | "marker"; id: string } | null;
export type SaveState = "saved" | "dirty" | "saving" | "error" | "conflict";

const HISTORY_MAX = 300;

/** How the timeline is laid out (remembered per browser). */
export interface TimelineView {
  /** "scene": only the layers visible during the scene under the playhead */
  scope: "scene" | "all";
  /** layers share rows when they don't overlap in time */
  pack: boolean;
  /** camera, markers, captions and comments as separate rows instead of one overview row */
  expanded: boolean;
  compact: boolean;
  /** moving a scene edge or an audio clip also moves everything after it (Shift flips it) */
  ripple: boolean;
}
const TL_KEY = "studio:timeline";
const TL_DEFAULT: TimelineView = { scope: "scene", pack: false, expanded: false, compact: false, ripple: false };
function loadTimelineView(): TimelineView {
  try {
    return { ...TL_DEFAULT, ...JSON.parse(localStorage.getItem(TL_KEY) ?? "{}") };
  } catch {
    return TL_DEFAULT;
  }
}

interface EditorState {
  project: Project | null;
  /** server revision the next save is based on (kept outside the project so undo can't rewind it) */
  revision: number;
  past: Project[];
  future: Project[];
  gesture: Project | null;
  saveState: SaveState;
  saveError: string;
  selection: Selection;
  /** further selected elements (multi-select); the primary is `selection` */
  multi: string[];
  time: number;
  playing: boolean;
  analysis: Record<string, AnalysisState>;
  jobs: Job[];
  /** reviewer comments from the share link */
  comments: ReviewComment[];
  panel: null | "render" | "import" | "history" | "compare" | "subtitles" | "share" | "renders" | "people" | "library" | "spread" | { analysis: string } | { hand: string } | { strokes: string } | { voice: string };
  overlays: { safe: boolean; thirds: boolean };
  /** zoomed-out view of the whole board (scene frames, camera keys) */
  boardView: boolean;
  timeline: TimelineView;

  open(p: Project): void;
  close(): void;
  edit(recipe: (d: Draft<Project>) => void): void;
  begin(): void;
  end(): void;
  undo(): void;
  redo(): void;
  select(s: Selection): void;
  setTime(t: number): void;
  setPlaying(p: boolean): void;
  set(partial: Partial<EditorState>): void;
  setTimeline(patch: Partial<TimelineView>): void;
}

export const useEditor = create<EditorState>((set, get) => ({
  project: null,
  revision: 0,
  past: [],
  future: [],
  gesture: null,
  saveState: "saved",
  saveError: "",
  selection: null,
  multi: [],
  time: 0,
  playing: false,
  analysis: {},
  jobs: [],
  comments: [],
  panel: null,
  overlays: { safe: false, thirds: false },
  boardView: false,
  timeline: loadTimelineView(),

  open: (p) =>
    set({ project: p, revision: p.revision, past: [], future: [], gesture: null, saveState: "saved", selection: null, multi: [], time: 0, playing: false, analysis: {}, jobs: [], panel: null }),
  close: () => set({ project: null, past: [], future: [] }),

  edit: (recipe) => {
    const { project, gesture, past } = get();
    if (!project) return;
    const next = produce(project, recipe);
    if (next === project) return;
    if (gesture) set({ project: next, saveState: "dirty" });
    else set({ project: next, past: [...past.slice(-HISTORY_MAX + 1), project], future: [], saveState: "dirty" });
  },
  begin: () => {
    const { project, gesture } = get();
    if (project && !gesture) set({ gesture: project });
  },
  end: () => {
    const { project, gesture, past } = get();
    if (!gesture) return;
    if (project !== gesture) set({ gesture: null, past: [...past.slice(-HISTORY_MAX + 1), gesture], future: [] });
    else set({ gesture: null });
  },
  undo: () => {
    const { project, past, future } = get();
    if (!project || past.length === 0) return;
    set({ project: past[past.length - 1], past: past.slice(0, -1), future: [project, ...future], saveState: "dirty", gesture: null });
    fixSelection();
  },
  redo: () => {
    const { project, past, future } = get();
    if (!project || future.length === 0) return;
    set({ project: future[0], past: [...past, project], future: future.slice(1), saveState: "dirty", gesture: null });
    fixSelection();
  },
  select: (s) => set({ selection: s, multi: [] }),
  setTime: (t) => set({ time: Math.max(0, t) }),
  setPlaying: (p) => set({ playing: p }),
  set: (partial) => set(partial),
  setTimeline: (patch) => {
    const timeline = { ...get().timeline, ...patch };
    set({ timeline });
    try {
      localStorage.setItem(TL_KEY, JSON.stringify(timeline));
    } catch {}
  },
}));

/** Drop the selection if undo/redo removed the selected object. */
function fixSelection() {
  const { selection, project } = useEditor.getState();
  if (!selection || !project) return;
  const lists: Record<string, { id: string }[]> = {
    element: project.elements,
    clip: project.audio,
    scene: project.scenes,
    camkey: project.camera.keyframes,
    marker: project.markers,
  };
  const exists = selection.kind === "camkey" && selection.id.startsWith("auto-") ? true : lists[selection.kind].some((x) => x.id === selection.id);
  if (!exists) useEditor.setState({ selection: null });
  const multi = useEditor.getState().multi.filter((id) => project.elements.some((e) => e.id === id));
  if (multi.length !== useEditor.getState().multi.length) useEditor.setState({ multi });
}

/** All selected element ids (primary first). */
export function selectedElements(): string[] {
  const { selection, multi } = useEditor.getState();
  const primary = selection?.kind === "element" ? [selection.id] : [];
  return [...primary, ...multi.filter((id) => !primary.includes(id))];
}

/** Click selection: replace, or (Shift/Ctrl) add/remove the element. */
export function selectElement(id: string, additive: boolean) {
  const st = useEditor.getState();
  if (!additive) return st.select({ kind: "element", id });
  const cur = selectedElements();
  if (cur.includes(id)) {
    const rest = cur.filter((x) => x !== id);
    useEditor.setState({ selection: rest.length ? { kind: "element", id: rest[0] } : null, multi: rest.slice(1) });
  } else useEditor.setState({ selection: { kind: "element", id }, multi: cur });
}

export function setSelectedElements(ids: string[]) {
  useEditor.setState({ selection: ids.length ? { kind: "element", id: ids[0] } : null, multi: ids.slice(1) });
}

export function uid(prefix: string): string {
  const a = new Uint8Array(4);
  crypto.getRandomValues(a);
  return prefix + Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}
